import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Label, Modal, Table } from 'semantic-ui-react';
import styled from '@emotion/styled';
import moment from 'moment';
import Spinner from 'components/ui/Spinner';
import ReviewModal from './ReviewModal';

export interface FeedbackEntry {
  id: number;
  propertyId: number;
  orderId: number;
  municipalityId: number;
  municipalityName: string;
  municipalityType: string;
  subMunicipalityId: number | null;
  subMunicipalityName: string | null;
  documentIds: string;
  aiExtractionJson: string;
  finalFormJson: string;
  reviewStatus: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  reviewedAt: string | null;
}

const Page = styled.div`
  padding: 32px;
  font-family: Lato, sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
`;

const Filters = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const MuniGroupHeader = styled.tr`
  background: #f0f0f0;
`;

const MuniGroupCell = styled.td`
  padding: 8px 12px !important;
  font-weight: bold;
  font-size: 13px;
  color: #333;
  border-top: 2px solid #d1d1d6 !important;
`;

const DocList = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const DocLink = styled.a`
  color: #ff5252;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
`;

const JsonPanel = styled.pre`
  background: #f5f5f5;
  border-radius: 4px;
  padding: 12px;
  font-size: 11px;
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  flex: 1;
  min-width: 0;
`;

const JsonRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
`;

const JsonLabel = styled.div`
  font-weight: bold;
  font-size: 13px;
  margin-bottom: 6px;
  color: #555;
`;

const NotesSection = styled.div`
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 16px;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: Lato, sans-serif;
  resize: vertical;
  min-height: 80px;
  color: #333;
  box-sizing: border-box;
`;

const NotesBadge = styled.span`
  display: inline-block;
  background: #fffde7;
  border: 1px solid #ffe082;
  border-radius: 3px;
  font-size: 11px;
  padding: 1px 5px;
  color: #795548;
  margin-left: 6px;
  vertical-align: middle;
`;

const statusOptions = [
  { key: 'pending', text: 'Pending Review', value: 'pending' },
  { key: 'mismatch', text: 'Mismatch', value: 'mismatch' },
  { key: 'match', text: 'Match', value: 'match' },
];

const statusColor = (s: string | null) => {
  if (s === 'Match') return 'green';
  if (s === 'Mismatch') return 'red';
  return 'grey';
};

const groupByLowestLevel = (entries: FeedbackEntry[]) => {
  const map = new Map<string, FeedbackEntry[]>();
  for (const entry of entries) {
    const key = entry.subMunicipalityId != null ? `sub_${entry.subMunicipalityId}` : `muni_${entry.municipalityId}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(entry);
  }
  return Array.from(map.values());
};

const TaxExtractionFeedback = () => {
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selected, setSelected] = useState<FeedbackEntry | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  useEffect(() => { load(); }, [statusFilter]);

  

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/orders/GetTaxExtractionFeedback', {
        params: { status: statusFilter },
      });
      setEntries(data);
    } finally {
      setLoading(false);
    }
  };

  

  const submitReview = async (id: number, reviewStatus: 'Match' | 'Mismatch') => {
    setSavingId(id);
    try {
      await axios.post('/api/orders/SetTaxExtractionFeedbackReview', { id, reviewStatus });
      setEntries(prev => prev.filter(e => e.id !== id));
      if (selected?.id === id) setSelected(null);
    } finally {
      setSavingId(null);
    }
  };

  

  const parseDocIds = (json: string): number[] => {
    try { return JSON.parse(json) as number[]; } catch { return []; }
  };

  const formatJson = (json: string) => {
    try { return JSON.stringify(JSON.parse(json), null, 2); } catch { return json; }
  };

  const groups = groupByLowestLevel(entries);


  return (
    <Page>
      <Header>
        <Title>Tax Extraction Feedback</Title>
        <Filters>
          <span style={{ fontSize: 13, color: '#666' }}>Show:</span>
          <Dropdown
            selection
            options={statusOptions}
            value={statusFilter}
            onChange={(_, { value }) => setStatusFilter(value as string)}
          />
          <span style={{ fontSize: 13, color: '#888' }}>{entries.length} record{entries.length !== 1 ? 's' : ''}</span>
        </Filters>
      </Header>

      {loading && <Spinner />}

      {!loading && entries.length === 0 && (
        <div style={{ color: '#888', fontSize: 14 }}>No records found.</div>
      )}

      {!loading && entries.length > 0 && (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Date</Table.HeaderCell>
              <Table.HeaderCell>HDS #</Table.HeaderCell>
              <Table.HeaderCell>Village</Table.HeaderCell>
              <Table.HeaderCell>Documents</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {groups.map(group => {
              const first = group[0];
              const muniLabel = first.subMunicipalityName
                ? `${first.subMunicipalityName} (${first.municipalityName || `ID ${first.municipalityId}`})`
                : (first.municipalityName || `ID ${first.municipalityId}`);
              const typeLabel = first.municipalityType ? ` · ${first.municipalityType}` : '';
              return (
                <React.Fragment key={first.municipalityId}>
                  <MuniGroupHeader>
                    <MuniGroupCell colSpan={6}>
                      {muniLabel}{typeLabel}
                      <span style={{ fontWeight: 'normal', color: '#666', marginLeft: 8, fontSize: 12 }}>
                        {group.length} record{group.length !== 1 ? 's' : ''}
                      </span>
                    </MuniGroupCell>
                  </MuniGroupHeader>
                  {group.map(entry => (
                    <Table.Row key={entry.id}>
                      <Table.Cell style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                        {moment(entry.createdAt).format('MM/DD/YY h:mm a')}
                      </Table.Cell>
                      <Table.Cell>
                        <a href={`/orders/order-information/${entry.orderId}/${entry.propertyId}`} target="_blank" rel="noreferrer" style={{ color: '#ff5252' }}>
                          HDS{entry.orderId}
                        </a>
                      </Table.Cell>
                      <Table.Cell style={{ fontSize: 13 }}>
                        {entry.subMunicipalityName ?? '—'}
                      </Table.Cell>
                      <Table.Cell>
                        <DocList>
                          {parseDocIds(entry.documentIds).map((docId, i) => (
                            <DocLink key={docId} href={`/api/documents/GetDocument/${docId}`} target="_blank" rel="noreferrer">
                              Doc {i + 1}
                            </DocLink>
                          ))}
                        </DocList>
                      </Table.Cell>
                      <Table.Cell>
                        <Label color={statusColor(entry.reviewStatus)} size="small">
                          {entry.reviewStatus ?? 'Pending'}
                        </Label>
                        {entry.notes && <NotesBadge title={entry.notes}>note</NotesBadge>}
                      </Table.Cell>
                      <Table.Cell>
                        <Button size="mini" onClick={() => setSelected(entry)}>View</Button>
                        {!entry.reviewStatus && (
                          <>
                            <Button
                              size="mini"
                              color="green"
                              loading={savingId === entry.id}
                              disabled={savingId === entry.id}
                              onClick={() => submitReview(entry.id, 'Match')}
                            >
                              Match
                            </Button>
                            <Button
                              size="mini"
                              color="red"
                              loading={savingId === entry.id}
                              disabled={savingId === entry.id}
                              onClick={() => submitReview(entry.id, 'Mismatch')}
                            >
                              Mismatch
                            </Button>
                          </>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      )}
{!!selected && (
        <ReviewModal
          selected={selected}
          setSelected={setSelected}
          entries={entries}
          setEntries={setEntries}
          savingId={savingId}
          setSavingId={setSavingId}
          submitReview={submitReview}
        />
      )}
      
    </Page>
  );
};

export default TaxExtractionFeedback;
